import {
  PinwheelLinkTokenInput,
  PinwheelLinkTokenResult,
  PinwheelLinkTokenResultTypenames,
  Query,
} from '@varo/graphql-schema-types';
import { useCallback, useState } from 'react';
import { SegmentInterface } from 'services/analytics/segment/types';
import PinWheelLinkToken from 'services/graphql/queries/PinWheelLinkToken';
import { useAnalyticsConfiguration } from 'shared/hooks/analytics/useAnalyticsConfiguration';
import { useGQLErrorFormatter } from 'shared/hooks/errorFormatters/useFormattedGQLErrors';
import { AbortError } from 'shared/hooks/useCancellableMutation/useCancellableMutation';
import useCancellableQuery, {
  NO_DATA_RETURNED,
} from 'shared/hooks/useCancellableQuery/useCancellableQuery';
import { GenericError } from 'shared/utils/errors/GenericError';
import { Maybe } from 'shared/utils/types';
import { CombinedError, OperationContext } from 'urql';

const MAX_RETRY = 3;
const NO_RESPONSE_RECEIVED = 'No response received from graphql';

interface PinwheelState {
  token: string;
  linkTokenId: string;
  /**
   * @deprecated use `requestLinkTokenError` instead
   */
  linkTokenError: boolean;
}
const initialPinwheelData: PinwheelState = {
  token: '',
  linkTokenId: '',
  linkTokenError: false,
};

type ExecutePinwheelLinkTokenQuery = (
  variables: { input?: PinwheelLinkTokenInput },
  context?: Partial<OperationContext>,
) => Promise<Pick<Query, 'pinwheelLinkToken'>>;

const requestLinkTokenImp = async (
  executePinwheelLinkTokenQuery: ExecutePinwheelLinkTokenQuery,
  input?: PinwheelLinkTokenInput,
  retries: number = MAX_RETRY,
): Promise<PinwheelLinkTokenResult | undefined> => {
  try {
    const data = await executePinwheelLinkTokenQuery(
      { input },
      { requestPolicy: 'network-only' },
    );

    return data.pinwheelLinkToken;
  } catch (error) {
    if (error instanceof AbortError) {
      throw error;
    }

    if (retries > 0) {
      return requestLinkTokenImp(
        executePinwheelLinkTokenQuery,
        input,
        retries - 1,
      );
    }

    throw error;
  }
};

const logErrorToSegment = (segmentClient: SegmentInterface, error: unknown) => {
  let errorType = 'PINWHEEL_ERROR';
  let errorMessage: string | undefined;
  let correlationId: string | undefined;

  if (error instanceof CombinedError) {
    errorType = error.networkError ? 'NETWORK_ERROR' : errorType;
    errorMessage = error.message;
    correlationId = error.graphQLErrors
      ?.flatMap((err) => err.extensions?.correlationId)?.[0]
      ?.toString();
  }

  void segmentClient.logEvent('Error Displayed', {
    event: 'Error Displayed',
    eventProperties: {
      page_name: 'Direct Deposit Dashboard',
      error_type: errorType,
      error_message: errorMessage,
      correlation_id: correlationId,
    },
  });
};

export const useRequestLinkToken = () => {
  const { segmentClient } = useAnalyticsConfiguration();
  const [pinwheelState, setPinwheelState] =
    useState<PinwheelState>(initialPinwheelData);
  const errorFormatter = useGQLErrorFormatter();
  const [requestLinkTokenError, setRequestLinkTokenError] =
    useState<Maybe<GenericError>>(null);
  const [executePinwheelLinkTokenQuery] =
    useCancellableQuery<Pick<Query, 'pinwheelLinkToken'>>(PinWheelLinkToken);

  const requestLinkToken = useCallback(
    async (input?: PinwheelLinkTokenInput) => {
      setPinwheelState(initialPinwheelData);
      setRequestLinkTokenError(null);

      try {
        const pinwheelData = await requestLinkTokenImp(
          executePinwheelLinkTokenQuery,
          input,
        );

        if (
          pinwheelData &&
          pinwheelData.__typename ===
            PinwheelLinkTokenResultTypenames.PinwheelLinkTokenSuccess
        ) {
          setPinwheelState({
            token: pinwheelData.token,
            linkTokenId: pinwheelData.linkTokenId,
            linkTokenError: false,
          });
        } else if (
          pinwheelData?.__typename ===
            PinwheelLinkTokenResultTypenames.PinwheelLinkTokenError ||
          !pinwheelData
        ) {
          setPinwheelState({
            ...initialPinwheelData,
            linkTokenError: true,
          });

          setRequestLinkTokenError(
            errorFormatter({
              errorLike: pinwheelData,
            }) ??
              new GenericError({
                message: NO_RESPONSE_RECEIVED,
              }),
          );
        }
      } catch (error) {
        if (error instanceof AbortError) {
          return;
        }

        setPinwheelState({
          ...initialPinwheelData,
          linkTokenError: true,
        });

        if (
          error instanceof CombinedError &&
          error.message.includes(NO_DATA_RETURNED)
        ) {
          setRequestLinkTokenError(
            new GenericError({
              message: NO_RESPONSE_RECEIVED,
            }),
          );
          return;
        }

        setRequestLinkTokenError(
          errorFormatter({
            combinedError: error instanceof CombinedError ? error : null,
          }) ??
            new GenericError({
              message: 'Unknown error occurred while fetching link token',
            }),
        );

        logErrorToSegment(segmentClient, error);
      }
    },
    [segmentClient, executePinwheelLinkTokenQuery, errorFormatter],
  );
  return {
    requestLinkToken,
    pinwheelState,
    requestLinkTokenError,
    setInitialPinwheelData: () => setPinwheelState(initialPinwheelData),
  };
};

export default useRequestLinkToken;
